import "./styles/globals.css";

import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";

import { isMarketingHost } from "@/lib/hosts";

/**
 * Client entry point. Replaces the legacy main.tsx.
 *
 * Decides which root to mount based on host (marketing vs seller subdomain /
 * custom domain) and pathname (marketing pages vs app/auth/dashboard pages).
 * Uses dynamic imports so Clerk/PostHog/Puck never reach the marketing entry's
 * initial chunk. See plan v10 § "Routing architecture".
 */

const APP_ROUTE_PATTERNS: RegExp[] = [
  /^\/dashboard(\/|$)/,
  /^\/login(\/|$)/,
  /^\/register(\/|$)/,
  /^\/waitlist(\/|$)/,
  /^\/onboarding(\/|$)/,
  /^\/upsell(\/|$)/,
  /^\/order(\/|$)/,
  /^\/order-complete(\/|$)/,
  /^\/stripe(\/|$)/,
  /^\/downloads(\/|$)/,
  /^\/p\/[^/]+/,
  /^\/__test__(\/|$)/,
];

function parseInitialData(): unknown {
  const node = document.getElementById("__SEO_DATA__");
  if (!node || !node.textContent) return undefined;
  try {
    return JSON.parse(node.textContent);
  } catch {
    return undefined;
  }
}

async function mountApp() {
  const { default: App } = await import("./App");
  const container = document.getElementById("root")!;
  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

async function mountMarketing() {
  const container = document.getElementById("root")!;
  const prerendered = document.documentElement.dataset.prerendered;

  if (prerendered === "head-only") {
    document.querySelectorAll("head [data-seo-static]").forEach((n) => n.remove());
  }

  const { MarketingPrerenderRoot } = await import("./marketing/MarketingPrerenderRoot");
  const initialData = parseInitialData();
  const tree = (
    <StrictMode>
      <MarketingPrerenderRoot initialData={initialData} url={window.location.pathname} />
    </StrictMode>
  );

  if (prerendered === "true") {
    hydrateRoot(container, tree, {
      onRecoverableError: (error) => {
        try {
          window.plausible?.("Hydration Error", {
            props: {
              route: window.location.pathname,
              msg: String(error).slice(0, 200),
            },
          });
        } catch {
          // swallow — never throw from a telemetry handler
        }
      },
    });
  } else {
    createRoot(container).render(tree);
  }
}

async function bootstrap() {
  const host = window.location.hostname;
  const path = window.location.pathname;
  const mountAsApp = !isMarketingHost(host) || APP_ROUTE_PATTERNS.some((re) => re.test(path));
  if (mountAsApp) {
    await mountApp();
  } else {
    await mountMarketing();
  }
}

void bootstrap();
